import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { TextInput as MatTextInput, TimePicker, DatePicker } from 'react-materialize';
import TextInput from '../atoms/textinput.jsx';
import Select from '../atoms/select.jsx';
import Checkbox from '../atoms/checkbox.jsx';
import Button from '../atoms/button.jsx';
import { Label } from '../atoms/label.jsx';
import Row from '../organism/row.jsx';
import Banner from '../organism/banner.jsx';
import LoadingSpinner from '../organism/spinner.jsx';
import { COLOR, SIZE, KEY, LABEL, FONT_FAMILY, SCREEN_MEDIA, URL } from '../../utils/constants.js';
import { useBookingData } from '../hooks/useBookingData.js';
import { useBookingPost } from '../hooks/useBookingPost.js';
import { useDecorationTypes } from '../hooks/useDecorationTypes.js';
import {
    getStep2MandatoryFields,
    getStep3MandatoryFields,
    convertImageToWebp,
    parseEventTime,
    EVENT_DATEPICKER_I18N
} from '../../utils/managereventbooking.js';


const Container = styled.div`
    overflow: auto; // To cover height with contents
    padding: 0px 15px;
    @media only screen and ${SCREEN_MEDIA.small} {
        margin: 0px 2px;
    }
    @media only screen and ${SCREEN_MEDIA.medium} {
        margin: 30px 80px;
    }
    @media only screen and ${SCREEN_MEDIA.long} {
        margin: 30px 150px;
    }
    @media only screen and ${SCREEN_MEDIA.xlong} {
        margin: 30px 250px;
    }
`;

const FormCard = styled.div`
    overflow: auto; // To cover height with contents
    padding: 25px;
    background-color: ${COLOR.background};
    border-radius: 25px;
    -webkit-box-shadow: -1px 0px 22px -3px rgba(153,153,153,1);
    -moz-box-shadow: -1px 0px 22px -3px rgba(153,153,153,1);
    box-shadow: -1px 0px 22px -3px rgba(153,153,153,1);
`;

const StepContent = styled.div`
    display: ${props => props.active ? 'block' : 'none'};
`;

const ClauseRow = styled.div`
    display: flex;
    align-items: flex-start;
`;

const PaymentMethodCard = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 15px;
    margin: 20px 0 0 0;
    border: 2px solid ${COLOR.ferra};
    border-radius: ${SIZE.inp_radious};
    img {
        width: 40px;
        height: 40px;
    }
`;

const CopyRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const CopyLink = styled.button`
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    font-family: ${FONT_FAMILY.avenirMedium};
    font-size: ${SIZE.caption};
    color: ${COLOR.secondary};
    text-decoration: underline;
    cursor: pointer;
    &:hover {
        opacity: 0.8;
    }
`;

const CashAmountRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 40px 0 0 0;
    > div:last-child {
        flex: 1;
    }
    .input-field {
        width: 100%;
        margin: 0;
        padding: 0;
    }
    .input-field input {
        margin: 0;
    }
`;

const BodyEventBooking = () => {

    const { data: typeList, isLoading: isTypesLoading } = useDecorationTypes();
    const { data: bookingData, isLoading: isBookingDataLoading } = useBookingData();

    const isLoading = isTypesLoading || isBookingDataLoading || !typeList || !bookingData;

    const typeCatLv1List = useMemo(() => {
        return typeList ? typeList.categoriesLv1 : [];
    }, [typeList]);

    const typeGenreList = useMemo(() => {
        return typeList ? typeList.genres : [];
    }, [typeList]);

    const { clauses, paymentMethods } = useMemo(() => {
        return {
            clauses: bookingData?.clauses ?? [],
            paymentMethods: bookingData?.paymentMethods ?? []
        };
    }, [bookingData]);

    const today = useMemo(() => new Date(), []);
    const navigate = useNavigate();
    const form = useRef();
    const formCard = useRef();
    const isFirstRender = useRef(true);
    const [step, setStep] = useState(1);
    const [isSending, setIsSending] = useState(false);
    const [formData, setFormData] = useState(null);
    const [selectedTypeCatLv1Id, setSelectedTypeCatLv1Id] = useState('');
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
    const [emptyFields, setEmptyFields] = useState({});
    const [clausesAccepted, setClausesAccepted] = useState(false);
    const [eventDate, setEventDate] = useState(null);

    const bookingPostResult = useBookingPost(isSending, formData)

    const resetForm = () => {
        form.current?.reset();
        setStep(1);
        setFormData(null);
        setSelectedTypeCatLv1Id('');
        setSelectedPaymentMethodId('');
        setEmptyFields({});
        setClausesAccepted(false);
        setEventDate(null);
    };

    useEffect(() => {
        if (!bookingPostResult || !('success' in bookingPostResult)) return;
        setIsSending(false)
        if (bookingPostResult.success) {
            window.M.toast({
                html: 'Upload successful!',
                classes: 'rounded green',
                displayLength: 3000
            })
            // Reset now so the form is already clean if the user navigates
            // back here (e.g. via the browser back button).
            resetForm();
            navigate(URL.thematics, { state: { bookingSuccess: true } });
        } else {
            window.M.toast({
                html: 'Upload error!',
                classes: 'red',
                displayLength: 3000
            })
        }
    }, [bookingPostResult, navigate])

    const copyToClipboard = (text, message) => {
        navigator.clipboard.writeText(text).then(() => {
            window.M.toast({
                html: message,
                classes: 'rounded',
                displayLength: 2000
            });
        }).catch(() => {
            window.M.toast({
                html: 'No se pudo copiar',
                classes: 'red',
                displayLength: 2000
            });
        });
    };

    // Fixing scroll on changing step
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        formCard.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [step]);

    // LoadingSpinner replaces the whole page content, so bring the user back
    // to the top when it's about to show - otherwise it renders off-screen
    // above whatever scroll position the form was at.
    useEffect(() => {
        if (isSending) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [isSending]);

    const clearEmptyField = useCallback((name) => {
        setEmptyFields(prevEmptyFields => {
            if (!prevEmptyFields[name]) return prevEmptyFields;
            const newEmptyFields = { ...prevEmptyFields };
            delete newEmptyFields[name];
            return newEmptyFields;
        });
    }, []);

    // react-materialize's DatePicker re-inits (destroying and recreating) the
    // whole M.Datepicker instance whenever its `options`/`onChange` props change
    // identity, so both must stay referentially stable across renders - otherwise
    // selecting a day (which triggers a state update/re-render) tears down the
    // calendar mid-interaction, looking like it closed itself and leaving
    // Materialize's scroll-lock overlay stuck.
    const eventDatePickerOptions = useMemo(() => ({
        format: 'dd mmm yyyy',
        minDate: today,
        i18n: EVENT_DATEPICKER_I18N
    }), [today]);

    const eventTimePickerOptions = useMemo(() => ({
        twelveHour: true,
        defaultTime: '6:00 PM',
        onCloseEnd: () => clearEmptyField('eventTime')
    }), [clearEmptyField]);

    // `date` is built by Materialize as local midnight of the clicked day
    // (`new Date(year, month, day)`), so it's kept as-is here rather than
    // going through `toISOString()` (which would convert to UTC and can
    // shift the day backward/forward depending on the browser's offset).
    const handleEventDateChange = useCallback((date) => {
        setEventDate(date);
        clearEmptyField('eventDate');
    }, [clearEmptyField]);

    const backStep = (e, step) => {
        e.preventDefault();
        setStep(step);
    };

    const validateStep1FormToGoStep2 = (e) => {
        e.preventDefault();
        if (!clausesAccepted) {
            setEmptyFields(prevEmptyFields => ({ ...prevEmptyFields, clausesAccepted: true }));
            return;
        }
        setStep(2);
    };

    const validateStep2FormToGoStep3 = (e) => {
        e.preventDefault();
        const newEmptyFields = {};
        getStep2MandatoryFields(selectedTypeCatLv1Id).forEach((name) => {
            const field = form.current.elements[name];
            if (!field || !field.value) { // Check if field is empty
                newEmptyFields[name] = true;
            }
        });
        setEmptyFields(newEmptyFields);
        if (Object.keys(newEmptyFields).length === 0) setStep(3);
    };

    const paymentMethodTitle = (item) => {
        if (item.holder) {
            return `${item.entity} - ${item.holder.toUpperCase()}`
        } else {
            return item.entity
        }
        
    }

    const handleSubmit = (e) => {
        e.preventDefault();

        // VALIDATING STEP 3 FORM TO HANDLE SUBMIT
        const newEmptyFields = {};
        getStep3MandatoryFields(selectedPaymentMethodId).forEach((name) => {
            const field = form.current.elements[name];
            if (!field || !field.value) { // Check if field is empty
                newEmptyFields[name] = true;
            }
        });
        setEmptyFields(newEmptyFields);
        if (Object.keys(newEmptyFields).length > 0) return;

        // PREPARING IMAGE CONVERTION
        // The screenshot input is not rendered for cash payments, so it may be
        // missing or empty; in that case the WebP conversion is skipped.
        const formEl = form.current;
        const paymentScreenshotInput = formEl.elements.paymentScreenshot;
        const originalPaymentScreenshot = paymentScreenshotInput?.files?.[0] ?? null;
        const preparePaymentScreenshot = originalPaymentScreenshot
            ? convertImageToWebp(originalPaymentScreenshot)
            : Promise.resolve(null);

        preparePaymentScreenshot
            .then((convertedPaymentScreenshot) => {
                if (convertedPaymentScreenshot) {
                    const dataTransfer = new DataTransfer();
                    dataTransfer.items.add(convertedPaymentScreenshot);
                    paymentScreenshotInput.files = dataTransfer.files;
                }
                const data = new FormData(formEl)
                // Multipart has no null value: omitting the field is how a null
                // paymentScreenshot is sent (drops the empty file entry, if any).
                if (!convertedPaymentScreenshot) data.delete('paymentScreenshot');

                const eventTimeValue = formEl.elements.eventTime?.value;
                const eventTime = parseEventTime(eventTimeValue);
                if (!eventTime) {
                    // Send the user back to step 2 with the mandatory-field error
                    // on the time picker instead of failing the whole submit.
                    console.log(`handleSubmit: unrecognized eventTime value: "${eventTimeValue}"`);
                    setEmptyFields({ eventTime: true });
                    setStep(2);
                    return;
                }
                const eventDateTime = new Date(
                    eventDate.getFullYear(),
                    eventDate.getMonth(), // return the index, not the mont. Jan -> 0, Sept -> 8
                    eventDate.getDate(),
                    eventTime.hours,
                    eventTime.minutes
                );
                data.set('eventDateTime', eventDateTime.toISOString());
                data.delete('eventDate');
                data.delete('eventTime');

                setFormData(data);
                setIsSending(true);
            })
            .catch((error) => {
                console.error(error);
                setIsSending(false);
                window.M.toast({
                    html: 'Ocurrió un error al enviar tu reserva',
                    classes: 'red',
                    displayLength: 3000
                });
            });
    };

    if (isLoading) return <LoadingSpinner/>;

    if (isSending) return <LoadingSpinner/>;

    const selectedPaymentMethod = paymentMethods.find((item) => String(item.id) === selectedPaymentMethodId);

    const celebrantNameTitle = Number(selectedTypeCatLv1Id) === 2 ? "Nombre del bebé" : "Nombre del festejado/a"

    const typeCatLv2List = selectedTypeCatLv1Id ? typeCatLv1List.find(obj => obj.id === Number(selectedTypeCatLv1Id)).categoriesLv2 : null

    const nameVerificationLabel = 
        <Label
            margin='0 0'
            text_size={SIZE.body}
            text_color={COLOR.secondary}
            font_family={FONT_FAMILY.avenirBlack}>
            Verifique que el nombre esté bien escrito, se usará para personalizar el evento.
        </Label>

    const emptyMandatoryLabel = 
        <Label
            margin='0 0'
            text_size={SIZE.body}
            text_color={COLOR.secondaryDark}
            font_family={FONT_FAMILY.avenirMedium}>
            Por favor, complete este campo.
        </Label>

    let stepTitle = '';
    switch (step) {
    case 1:
        stepTitle = 'Paso 1/3: Revise las cláusulas';
        break;
    case 2:
        stepTitle = 'Paso 2/3: Cuéntanos sobre tu evento';
        break;
    default:
        stepTitle = 'Paso 3/3: Confirma el pago';
        break;
    }

    return (
        <div>
            <Banner title='Reserva tu evento'/>

            <Container>
                <Row margin='40px 0px'>
                    <FormCard ref={formCard}>
                        <Label
                            margin='0 0 30px 0'
                            text_size={SIZE.title}
                            text_color={COLOR.ferra}
                            font_family={FONT_FAMILY.avenirMedium}>
                            { stepTitle }
                        </Label>

                        <form ref={form} onSubmit={handleSubmit}>

                            <StepContent active={step === 1}>
                                {
                                    clauses.map((item, index) => {
                                        return (
                                            <ClauseRow key={index}>
                                                <Label
                                                    text_color={ COLOR.ferra }
                                                    text_size={SIZE.body}
                                                    font_family={FONT_FAMILY.avenirMedium}
                                                    margin='12px 8px 12px 0'>
                                                    { `${index+1}.` }
                                                </Label>
                                                <Label
                                                    text_color={ COLOR.ferra }
                                                    text_size={SIZE.body}
                                                    font_family={FONT_FAMILY.avenirMedium}
                                                    margin='12px 0'>
                                                    { item.description }
                                                </Label>
                                            </ClauseRow>
                                        )
                                    })
                                }
                                <Checkbox
                                    id='clausesAccepted'
                                    name='clausesAccepted'
                                    margin='20px 0 0 0'
                                    float='none'
                                    text_size={SIZE.subtitle}
                                    text_color={COLOR.ferra}
                                    font_family={FONT_FAMILY.avenirMedium}
                                    checked={ clausesAccepted }
                                    onChange={ (checked) => {
                                        setClausesAccepted(checked);
                                        if (checked) clearEmptyField('clausesAccepted');
                                    }}>
                                    He leído las cláusulas
                                </Checkbox>
                                { emptyFields.clausesAccepted &&
                                    <Label
                                        margin='0 0'
                                        text_size={SIZE.body}
                                        text_color={COLOR.secondaryDark}
                                        font_family={FONT_FAMILY.avenirMedium}>
                                        Este campo es obligatorio
                                    </Label>
                                }
                                <Button
                                    type='button'
                                    width='140px'
                                    height='46px'
                                    bg_color={ COLOR.primary }
                                    margin='30px 0 0 0'
                                    float='right'
                                    onClick={ validateStep1FormToGoStep2 }>
                                    <Label
                                        text_color={ COLOR.ferra }
                                        text_size={SIZE.subtitle}
                                        font_family={FONT_FAMILY.avenirMedium}
                                        margin='auto'>
                                        {LABEL.next}
                                    </Label>
                                </Button>
                            </StepContent>

                            <StepContent active={step === 2}>
                                <TextInput
                                    id='organizerName'
                                    name='organizerName'
                                    type='text'
                                    label='Tu nombre*'
                                    placeholder='María Cifuentes'
                                    margin='40px 0 0 0'
                                    onChange={ () => clearEmptyField('organizerName') }/>
                                { emptyFields.organizerName && emptyMandatoryLabel }
                                <TextInput
                                    id='phone'
                                    name='phone'
                                    type='tel'
                                    label='Tu Teléfono / WhatsApp*'
                                    placeholder='944555666'
                                    margin='40px 0 0 0'
                                    onChange={ () => clearEmptyField('phone') }/>
                                { emptyFields.phone && emptyMandatoryLabel }
                                <Select
                                    id='typeCatLv1'
                                    name='typeCatLv1'
                                    multiple={false}
                                    margin='30px 0 0 0'
                                    onChange={ (event) => {
                                        setSelectedTypeCatLv1Id(event.target.value)
                                        clearEmptyField('typeCatLv1')
                                    }}
                                    options={{
                                        dropdownOptions: {
                                            closeOnClick: true,
                                            container: document.body
                                        }
                                    }}>
                                    <option value='' disabled selected>Tipo de evento*</option>
                                    {
                                        typeCatLv1List.map((item, index) => {
                                            return <option value={item.id}>{item.title}</option>
                                        })
                                    }
                                </Select>
                                { emptyFields.typeCatLv1 && emptyMandatoryLabel }
                                {
                                    [3, 4, 6].includes(Number(selectedTypeCatLv1Id)) &&
                                    <Select
                                        id='typeCatLv2'
                                        name='typeCatLv2'
                                        multiple={false}
                                        margin='30px 0 0 0'
                                        onChange={ () => {}}
                                        options={{
                                            dropdownOptions: {
                                                closeOnClick: true,
                                                container: document.body
                                            }
                                        }}>
                                        <option value='' selected>Subtipo (Opcional)</option>
                                        {
                                            typeCatLv2List.map((item, index) => {
                                                return <option value={item.id}>{item.title}</option>
                                            })
                                        }
                                    </Select>
                                }
                                {
                                    Number(selectedTypeCatLv1Id) === 1 &&
                                    <div>
                                        <TextInput
                                            id='birthNumber'
                                            name='birthNumber'
                                            type='number'
                                            label='Número de cumpleaños*'
                                            placeholder='7'
                                            margin='40px 0 0 0'
                                            onChange={ () => clearEmptyField('birthNumber') }/>
                                        { emptyFields.birthNumber && emptyMandatoryLabel }
                                    </div>
                                }
                                {
                                    [1, 2].includes(Number(selectedTypeCatLv1Id)) &&
                                    <div>
                                        <TextInput
                                            id='celebrantName'
                                            name='celebrantName'
                                            type='text'
                                            label={ celebrantNameTitle }
                                            placeholder='Khaleesi'
                                            margin='40px 0 0 0'
                                            onChange={ () => clearEmptyField('celebrantName') }/>
                                        { emptyFields.celebrantName && emptyMandatoryLabel }
                                        { nameVerificationLabel }
                                    </div>
                                }
                                {
                                    (Number(selectedTypeCatLv1Id) === 4) &&
                                    <div>
                                        <TextInput
                                            id='couplePerson1Name'
                                            name='couplePerson1Name'
                                            type='text'
                                            label='Pareja. Persona 1. Nombre*'
                                            placeholder='Gabriel'
                                            margin='40px 0 0 0'
                                            onChange={ () => clearEmptyField('couplePerson1Name') }/>
                                        { emptyFields.couplePerson1Name && emptyMandatoryLabel }
                                        { nameVerificationLabel }
                                        <TextInput
                                            id='couplePerson2Name'
                                            name='couplePerson2Name'
                                            type='text'
                                            label='Pareja. Persona 2. Nombre*'
                                            placeholder='Valentina'
                                            margin='40px 0 0 0'
                                            onChange={ () => clearEmptyField('couplePerson2Name') }/>
                                        { emptyFields.couplePerson2Name && emptyMandatoryLabel }
                                        { nameVerificationLabel }
                                    </div>
                                }
                                <div style={{ display: 'flex', margin: '40px 0 0 0' }}>
                                    <DatePicker
                                        id='eventDateDisplay'
                                        label='Fecha*'
                                        placeholder='26 sept. 2026'
                                        options={eventDatePickerOptions}
                                        onChange={handleEventDateChange}/>
                                </div>
                                { /* DatePicker shows a localized display string; this hidden field only
                                     tracks whether a date was picked, for mandatory-field validation.
                                     It is removed from the submitted payload in favor of `eventDateTime`,
                                     built at submit time from `eventDate` + `eventTime`. */ }
                                <input type='hidden' id='eventDate' name='eventDate' value={eventDate ? eventDate.toDateString() : ''} readOnly/>
                                { emptyFields.eventDate && emptyMandatoryLabel }
                                <div style={{ display: 'flex', margin: '40px 0 0 0' }}>
                                    <TimePicker
                                        id='eventTime'
                                        name='eventTime'
                                        label='Hora*'
                                        placeholder='6:00 PM'
                                        options={eventTimePickerOptions}/>
                                </div>
                                { emptyFields.eventTime && emptyMandatoryLabel }
                                <TextInput
                                    id='address'
                                    name='address'
                                    type='text'
                                    label='Dirección*'
                                    placeholder='Av. Pacífico 350'
                                    defaultValue='Por confirmar'
                                    margin='40px 0 0 0'
                                    onChange={ () => clearEmptyField('address') }/>
                                { emptyFields.address && emptyMandatoryLabel }
                                <TextInput
                                    id='placeName'
                                    name='placeName'
                                    type='text'
                                    label='Lugar (Opcional)'
                                    placeholder='Local Lumaja, Iglesia evangélica, etc'
                                    margin='40px 0 0 0'
                                    onChange={ () => {} }/>

                                <Button
                                    type='button'
                                    width='120px'
                                    height='46px'
                                    bg_color={ COLOR.grayClear }
                                    margin='30px 10px 0 0'
                                    float='left'
                                    onClick={ (e) => { backStep(e, 1) } }>
                                    <Label
                                        text_color={ COLOR.ferra }
                                        text_size={SIZE.subtitle}
                                        font_family={FONT_FAMILY.noteworthyBold}
                                        margin='auto'>
                                        {LABEL.back}
                                    </Label>
                                </Button>
                                <Button
                                    type='button'
                                    width='140px'
                                    height='46px'
                                    bg_color={ COLOR.primary }
                                    margin='30px 0 0 0'
                                    float='right'
                                    onClick={ validateStep2FormToGoStep3 }>
                                    <Label
                                        text_color={ COLOR.ferra }
                                        text_size={SIZE.subtitle}
                                        font_family={FONT_FAMILY.noteworthyBold}
                                        margin='auto'>
                                        {LABEL.next}
                                    </Label>
                                </Button>
                            </StepContent>

                            <StepContent active={step === 3}>
                                <Label
                                    margin='0 0 20px 0'
                                    text_size={SIZE.subtitle}
                                    text_color={COLOR.black}
                                    font_family={FONT_FAMILY.avenirMedium}>
                                    Elige un método de pago
                                </Label>
                                <Select
                                    id='paymentMethodId'
                                    name='paymentMethodId'
                                    multiple={false}
                                    margin='0 0'
                                    value={selectedPaymentMethodId}
                                    onChange={ (event) => {
                                        setSelectedPaymentMethodId(event.target.value)
                                        clearEmptyField('paymentMethodId')
                                    }}
                                    options={{
                                        dropdownOptions: {
                                            closeOnClick: true,
                                            container: document.body
                                        }
                                    }}>
                                    <option value='' disabled selected>Selecciona un método de pago*</option>
                                    {
                                        paymentMethods.map((item) => (
                                            <option key={item.id} value={item.id} data-icon={item.icon}>
                                                { paymentMethodTitle(item) }
                                            </option>
                                        ))
                                    }
                                </Select>
                                { emptyFields.paymentMethodId && emptyMandatoryLabel }

                                { selectedPaymentMethod &&
                                    <div>
                                        <PaymentMethodCard>
                                        <img src={selectedPaymentMethod.icon} alt={selectedPaymentMethod.entity}/>
                                        <div>
                                            <Label
                                                margin='0 0'
                                                text_size={SIZE.body}
                                                text_color={COLOR.ferra}
                                                font_family={FONT_FAMILY.avenirBlack}>
                                                { `${selectedPaymentMethod.entity}` }
                                            </Label>
                                            { (Number(selectedPaymentMethod.id) !== 7) &&
                                            <div>
                                                <CopyRow>
                                                    <Label
                                                        margin='0 0'
                                                        text_size={SIZE.body}
                                                        text_color={COLOR.ferra}
                                                        font_family={FONT_FAMILY.avenirBlack}>
                                                        { `${selectedPaymentMethod.number}` }
                                                    </Label>
                                                    <CopyLink
                                                        type='button'
                                                        onClick={ () => copyToClipboard(selectedPaymentMethod.number, 'Número copiado') }>
                                                        { LABEL.copy }
                                                    </CopyLink>
                                                </CopyRow>
                                                <CopyRow>
                                                    <Label
                                                        margin='4px 0 0 0'
                                                        text_size={SIZE.caption}
                                                        text_color={COLOR.grayDark}
                                                        font_family={FONT_FAMILY.avenirMedium}>
                                                        { selectedPaymentMethod.holder?.toUpperCase() }
                                                    </Label>
                                                    <CopyLink
                                                        type='button'
                                                        onClick={ () => copyToClipboard(selectedPaymentMethod.holder?.toUpperCase(), 'Nombre copiado') }>
                                                        { LABEL.copy }
                                                    </CopyLink>
                                                </CopyRow>
                                            </div>
                                            }
                                        </div>
                                        </PaymentMethodCard>

                                        { (Number(selectedPaymentMethod.id) !== 7) &&
                                        <div>
                                            <Label
                                                margin='20px 0 0 0'
                                                text_size={SIZE.subtitle}
                                                text_color={COLOR.black}
                                                font_family={FONT_FAMILY.avenirMedium}>
                                                Adjunta la captura de pago
                                            </Label>
                                            <MatTextInput
                                                id='paymentScreenshot'
                                                name='paymentScreenshot'
                                                type='file'
                                                label='Imagen'
                                                accept='image/png, image/jpeg'
                                                noLayout
                                                onChange={ () => clearEmptyField('paymentScreenshot') }/>
                                            { emptyFields.paymentScreenshot && emptyMandatoryLabel }
                                        </div>
                                        }
                                        { (Number(selectedPaymentMethod.id) === 7) &&
                                        <div>
                                            <CashAmountRow>
                                                <Label
                                                    margin='0'
                                                    text_size={SIZE.subtitle}
                                                    text_color={COLOR.black}
                                                    font_family={FONT_FAMILY.avenirBlack}>
                                                    S/.
                                                </Label>
                                                <TextInput
                                                    id='paymentCashAmount'
                                                    name='paymentCashAmount'
                                                    type='text'
                                                    inputMode='decimal'
                                                    pattern='[0-9]*[.,]?[0-9]*'
                                                    label='Ingresa el monto en efectivo*'
                                                    placeholder='150'
                                                    onChange={ () => clearEmptyField('paymentCashAmount') }/>
                                            </CashAmountRow>
                                            { emptyFields.paymentCashAmount && emptyMandatoryLabel }
                                        </div>
                                        }
                                    </div>
                                }

                                <Button
                                    type='button'
                                    width='120px'
                                    height='46px'
                                    bg_color={ COLOR.grayClear }
                                    margin='30px 10px 0 0'
                                    float='left'
                                    onClick={ (e) => { backStep(e, 2) } }>
                                    <Label
                                        text_color={ COLOR.ferra }
                                        text_size={SIZE.subtitle}
                                        font_family={FONT_FAMILY.noteworthyBold}
                                        margin='auto'>
                                        Atrás
                                    </Label>
                                </Button>
                                <Button
                                    type='submit'
                                    width='120px'
                                    height='46px'
                                    bg_color={ COLOR.primary }
                                    margin='30px 0 0 0'
                                    float='right'
                                    onChange={ () => {} }>
                                    <Label
                                        text_color={ COLOR.ferra }
                                        text_size={SIZE.subtitle}
                                        font_family={FONT_FAMILY.noteworthyBold}
                                        margin='auto'>
                                        {LABEL.send}
                                    </Label>
                                </Button>
                            </StepContent>

                        </form>
                    </FormCard>
                </Row>
            </Container>
        </div>
    );
};

export default BodyEventBooking;
